/**
 * StyledTextArea Component
 *
 * A theme-aware multiline text area component with built-in field-level validation.
 * Uses Material-UI v7 TextField with multiline support and dynamic theme integration.
 *
 * IMPORTANT:
 * - Uses ONLY theme tokens (NO hardcoded values)
 * - Handles validation at field level (NOT section level)
 * - Supports auto-resize, character count, and multiline features
 * - Optimized with React.memo
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  TextField,
  TextFieldProps,
  useTheme,
  Box,
  Typography,
  SxProps,
  Theme
} from '@mui/material';
import {
  validateFieldAsync,
  ValidationRule,
  ValidationError
} from '../../validation';

// ============================================================================
// COMPONENT INTERFACES
// ============================================================================

export interface StyledTextAreaProps {
  /**
   * Core Functionality
   */
  value: string;
  onChange: (value: string) => void;

  /**
   * Form Integration
   */
  name?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;

  /**
   * TextArea Configuration
   */
  rows?: number;
  maxRows?: number;
  minRows?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  autoSelect?: boolean;
  spellCheck?: boolean;

  /**
   * Resize Behavior
   */
  resizable?: boolean;
  autoResize?: boolean;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';

  /**
   * Content Constraints
   */
  maxLength?: number;
  minLength?: number;
  showCharacterCount?: boolean;
  wordWrap?: boolean;

  /**
   * Validation (Field-Level Responsibility)
   */
  error?: boolean;
  errorMessage?: string;
  rules?: ValidationRule[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  onValidationChange?: (errors: ValidationError[]) => void;

  /**
   * Theme Integration (REQUIRED - no hardcoded styles)
   */
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

  /**
   * Accessibility
   */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;

  /**
   * Event Handlers
   */
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLDivElement>) => void;

  /**
   * Custom Styling (Theme-Based)
   */
  sx?: SxProps<Theme>;

  /**
   * Custom Validation
   */
  customValidation?: (value: string) => boolean;

  /**
   * Additional TextField Props (for compatibility)
   */
  id?: string;
  fullWidth?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  InputLabelProps?: any;
  FormHelperTextProps?: any;
  inputRef?: React.Ref<any>;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const StyledTextArea = React.memo<StyledTextAreaProps>(({
  // Core props
  value,
  onChange,
  name,
  label,
  placeholder,
  helperText,
  required = false,
  disabled = false,
  readOnly = false,

  // TextArea configuration
  rows = 4,
  maxRows,
  minRows = 1,
  autoComplete,
  autoFocus = false,
  autoSelect = false,
  spellCheck,

  // Resize behavior
  resizable = true,
  autoResize = false,
  resize = 'vertical',

  // Content constraints
  maxLength,
  minLength,
  showCharacterCount = false,
  wordWrap = true,

  // Validation
  error: externalError = false,
  errorMessage,
  rules = [],
  validateOnChange = false,
  validateOnBlur = true,
  onValidationChange,

  // Theme integration
  variant = 'outlined',
  size = 'medium',
  color = 'primary',

  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,

  // Event handlers
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,

  // Custom styling
  sx,

  // Custom validation
  customValidation,

  // Other props
  ...otherProps
}) => {
  const theme = useTheme();

  // Extract inputRef to avoid type conflicts between React versions
  const { inputRef, ...safeOtherProps } = otherProps as any;

  // ============================================================================
  // VALIDATION STATE
  // ============================================================================

  const [internalErrors, setInternalErrors] = useState<ValidationError[]>([]);

  // Combine external error with internal validation errors
  const hasError = externalError || internalErrors.length > 0;
  const displayErrorMessage = errorMessage || (internalErrors[0]?.message);

  // ============================================================================
  // CHARACTER COUNT LOGIC
  // ============================================================================

  const characterCount = value.length;
  const characterCountText = maxLength
    ? `${characterCount}/${maxLength}`
    : `${characterCount}`;

  // Character count color based on usage
  const characterCountColor = useMemo(() => {
    if (!maxLength) return theme.palette.text.secondary;
    const usage = characterCount / maxLength;
    if (usage >= 1) return theme.palette.error.main;
    if (usage >= 0.8) return theme.palette.warning.main;
    return theme.palette.text.secondary;
  }, [characterCount, maxLength, theme]);

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================

  const validateValue = useCallback(async (valueToValidate: string) => {
    if (rules.length === 0 && !customValidation) {
      return [];
    }

    try {
      // Build validation rules
      const validationRules = [...rules];

      // Add custom validation if provided
      if (customValidation) {
        validationRules.push({
          type: 'custom',
          validator: customValidation,
          message: 'Invalid value'
        });
      }

      // Validate the field
      const errors = await validateFieldAsync(valueToValidate, validationRules, name || 'field');

      setInternalErrors(errors);
      onValidationChange?.(errors);

      return errors;
    } catch (error) {
      console.error('Validation error:', error);
      return [];
    }
  }, [rules, customValidation, name, onValidationChange]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    // Apply maxLength constraint
    if (maxLength && newValue.length > maxLength) {
      return; // Don't allow input beyond maxLength
    }

    onChange(newValue);

    // Validate on change if enabled
    if (validateOnChange) {
      validateValue(newValue);
    }
  }, [onChange, maxLength, validateOnChange, validateValue]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    onBlur?.(event);

    // Validate on blur if enabled
    if (validateOnBlur) {
      validateValue(value);
    }
  }, [onBlur, validateOnBlur, validateValue, value]);

  const handleFocus = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocus?.(event);

    // Auto-select text if enabled
    if (autoSelect && event.target) {
      event.target.select();
    }
  }, [onFocus, autoSelect]);

  // ============================================================================
  // THEME-AWARE STYLING
  // ============================================================================

  const fieldStyles = useMemo((): SxProps<Theme> => ({
    // Base styles using theme tokens
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider,
      },

      '&:hover fieldset': {
        borderColor: theme.palette.text.primary,
      },

      '&.Mui-focused fieldset': {
        borderColor: theme.palette[color].main,
      },

      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },

      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        '& fieldset': {
          borderColor: theme.palette.action.disabled,
        },
      },

      // TextArea specific styles
      '& textarea': {
        resize: resizable ? resize : 'none',
        wordWrap: wordWrap ? 'break-word' : 'normal',
        whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
      },
    },

    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.action.hover,

      '&:hover': {
        backgroundColor: theme.palette.action.focus,
      },

      '&.Mui-focused': {
        backgroundColor: theme.palette.action.hover,
      },

      '&.Mui-error': {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      },

      // TextArea specific styles
      '& textarea': {
        resize: resizable ? resize : 'none',
        wordWrap: wordWrap ? 'break-word' : 'normal',
        whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
      },
    },

    '& .MuiInput-root': {
      '&:before': {
        borderBottomColor: theme.palette.divider,
      },

      '&:hover:not(.Mui-disabled):before': {
        borderBottomColor: theme.palette.text.primary,
      },

      '&.Mui-focused:after': {
        borderBottomColor: theme.palette[color].main,
      },

      '&.Mui-error:after': {
        borderBottomColor: theme.palette.error.main,
      },

      // TextArea specific styles
      '& textarea': {
        resize: resizable ? resize : 'none',
        wordWrap: wordWrap ? 'break-word' : 'normal',
        whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
      },
    },

    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,

      '&.Mui-focused': {
        color: theme.palette[color].main,
      },

      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    '& .MuiFormHelperText-root': {
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(1.75),
      marginTop: theme.spacing(0.375),

      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    // Custom sx prop overrides
    ...sx,
  }), [theme, color, resizable, resize, wordWrap, sx]);

  // ============================================================================
  // ACCESSIBILITY ATTRIBUTES
  // ============================================================================

  const accessibilityProps = useMemo(() => ({
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy || (displayErrorMessage ? `${name}-error` : undefined),
    'aria-invalid': ariaInvalid || hasError,
    'aria-required': required,
  }), [ariaLabel, ariaDescribedBy, ariaInvalid, hasError, required, name, displayErrorMessage]);

  // ============================================================================
  // INPUT PROPS
  // ============================================================================

  const inputProps = useMemo(() => ({
    maxLength,
    minLength,
    spellCheck,
    autoComplete,
    readOnly,
  }), [maxLength, minLength, spellCheck, autoComplete, readOnly]);

  // ============================================================================
  // HELPER TEXT WITH CHARACTER COUNT
  // ============================================================================

  const combinedHelperText = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Add original helper text
    if (displayErrorMessage || helperText) {
      parts.push(displayErrorMessage || helperText);
    }

    // Add character count if enabled
    if (showCharacterCount) {
      if (parts.length > 0) {
        parts.push(' ');
      }
      parts.push(
        <span key="char-count" style={{ color: characterCountColor }}>
          {characterCountText}
        </span>
      );
    }

    return parts.length > 0 ? (
      <span>
        {parts.map((part, index) =>
          typeof part === 'string' ? part : React.cloneElement(part as React.ReactElement, { key: index })
        )}
      </span>
    ) : undefined;
  }, [displayErrorMessage, helperText, showCharacterCount, characterCountColor, characterCountText]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <TextField
      {...safeOtherProps}
      inputRef={inputRef}

      // Core props
      value={value}
      onChange={handleChange}
      name={name}
      label={label}
      placeholder={placeholder}
      required={required}
      disabled={disabled}

      // TextArea configuration
      multiline={true}
      rows={autoResize ? undefined : rows}
      maxRows={autoResize ? maxRows : undefined}
      minRows={autoResize ? minRows : undefined}
      autoFocus={autoFocus}

      // Theme integration
      variant={variant}
      size={size}
      color={color}

      // Validation state
      error={hasError}
      helperText={combinedHelperText}

      // Event handlers
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}

      // Styling
      sx={fieldStyles}

      // Input props
      slotProps={{
        inputLabel: {
          sx: {
            color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
          }
        },
        formHelperText: {
          id: displayErrorMessage ? `${name}-error` : undefined,
          sx: {
            color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
          }
        }
      }}
      inputProps={{
        ...inputProps,
        ...accessibilityProps,
        ...(maxLength && { maxLength }), // Add HTML maxLength attribute
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo optimization
  // Only re-render if relevant props change

  const relevantProps = [
    'value', 'error', 'errorMessage', 'disabled', 'readOnly', 'required',
    'label', 'placeholder', 'helperText', 'variant', 'size', 'color',
    'rows', 'maxRows', 'minRows', 'autoResize', 'resizable', 'resize',
    'maxLength', 'showCharacterCount', 'wordWrap'
  ] as const;

  for (const prop of relevantProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false; // Props changed, re-render needed
    }
  }

  // Check validation rules array
  if (JSON.stringify(prevProps.rules) !== JSON.stringify(nextProps.rules)) {
    return false;
  }

  // Check sx prop
  if (JSON.stringify(prevProps.sx) !== JSON.stringify(nextProps.sx)) {
    return false;
  }

  return true; // Props haven't changed, skip re-render
});

StyledTextArea.displayName = 'StyledTextArea';

// ============================================================================
// EXPORTS
// ============================================================================

export { StyledTextArea as default };